/**
 * Batched Wikipedia thumbnails for the city browse grid.
 *
 * Ported from `apps/native/src/utils/wiki-images.ts`. Desktop already had a
 * single-title fetcher inside `PopularCities`, but the onboarding grid mounts
 * every card at once — one request per card would be ~120 requests — so this is
 * the batched form: one request per 50 titles, cached module-level including
 * misses.
 */

const WIKI_API_ENDPOINT = "https://en.wikipedia.org/w/api.php";

/** The API's own cap on titles per query for anonymous callers. */
const WIKI_TITLES_PER_REQUEST = 50;

type WikiThumbnail = { source: string; width: number; height: number };

type WikiPage = {
  pageid: number;
  title: string;
  thumbnail?: WikiThumbnail;
};

type WikiTitleMapping = { from: string; to: string };

type WikiBatchResponse = {
  query?: {
    /** Case/underscore fixes, e.g. "new york" → "New york". */
    normalized?: WikiTitleMapping[];
    /** Redirects followed, e.g. "NYC" → "New York City". */
    redirects?: WikiTitleMapping[];
    pages?: Record<string, WikiPage>;
  };
};

/**
 * Resolved thumbnails, keyed `"<size>|<title>"`. A `null` is cached too — a
 * city with no usable image should not be re-requested on every mount.
 */
const thumbnailCache = new Map<string, string | null>();

/** Walks a title through the normalise/redirect maps to its final page title. */
const followTitle = (title: string, hops: Map<string, string>): string => {
  let current = title;
  // Chains are one or two long; the cap is only there in case the API loops.
  for (let step = 0; step < 5; step += 1) {
    const next = hops.get(current);
    if (!next || next === current) break;
    current = next;
  }
  return current;
};

const fetchThumbnailBatch = async (
  titles: string[],
  thumbnailSize: number,
): Promise<Map<string, string | null>> => {
  const resolved = new Map<string, string | null>();

  const params = new URLSearchParams({
    action: "query",
    titles: titles.join("|"),
    prop: "pageimages",
    format: "json",
    pithumbsize: String(thumbnailSize),
    // `pilimit` defaults to 1, which would return an image for the first page
    // only — the whole point of batching is lost without this.
    pilimit: String(WIKI_TITLES_PER_REQUEST),
    origin: "*",
    redirects: "1",
  });

  const response = await fetch(`${WIKI_API_ENDPOINT}?${params.toString()}`);
  if (!response.ok) {
    throw new Error(`Network response was not ok: ${response.statusText}`);
  }

  const data: WikiBatchResponse = await response.json();

  const hops = new Map<string, string>();
  data.query?.normalized?.forEach(({ from, to }) => hops.set(from, to));
  data.query?.redirects?.forEach(({ from, to }) => hops.set(from, to));

  const pageByTitle = new Map<string, WikiPage>();
  Object.values(data.query?.pages ?? {}).forEach((page) => {
    if (page?.title) pageByTitle.set(page.title, page);
  });

  titles.forEach((title) => {
    const page = pageByTitle.get(followTitle(title, hops));
    resolved.set(title, page?.thumbnail?.source ?? null);
  });

  return resolved;
};

/**
 * Thumbnails for many titles at once.
 *
 * Returns a map keyed by the exact strings passed in. A title with no page, or
 * a page with no image, resolves to `null`.
 */
export const getWikipediaImages = async (
  searchTerms: string[],
  thumbnailSize: number = 400,
): Promise<Record<string, string | null>> => {
  const cacheKey = (title: string) => `${thumbnailSize}|${title}`;

  const wanted = Array.from(
    new Set(searchTerms.map((term) => term.trim()).filter(Boolean)),
  );

  const result: Record<string, string | null> = {};
  const missing: string[] = [];

  wanted.forEach((title) => {
    const cached = thumbnailCache.get(cacheKey(title));
    if (cached !== undefined) {
      result[title] = cached;
    } else {
      missing.push(title);
    }
  });

  const chunks: string[][] = [];
  for (let i = 0; i < missing.length; i += WIKI_TITLES_PER_REQUEST) {
    chunks.push(missing.slice(i, i + WIKI_TITLES_PER_REQUEST));
  }

  // In parallel: a full city grid is two or three chunks, and running them one
  // after another would hold the whole grid on its spinner for no reason.
  await Promise.all(
    chunks.map(async (chunk) => {
      try {
        const resolved = await fetchThumbnailBatch(chunk, thumbnailSize);
        resolved.forEach((source, title) => {
          thumbnailCache.set(cacheKey(title), source);
          result[title] = source;
        });
      } catch (error) {
        console.error("Error fetching Wikipedia images:", error);
        // Left uncached so a transient failure retries on the next mount.
        chunk.forEach((title) => {
          result[title] = null;
        });
      }
    }),
  );

  return result;
};
